import React, { useState, useCallback, useEffect } from 'react';

import {
  PageHeader,
  type PageHeaderAction,
} from '../../../../components/PageHeader';
import { TableHeader as CustomTableHeader } from '../../../../components/Table';
import { useDebounce } from '../../../../hooks';
import { useSearchOrders } from '../../hooks';
import { OrderDetailsPanel } from '../OrderDetails';

import { OrderListErrorState, OrderTable, OrderPagination } from './components';

export const OrderList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const pageSize = 25;

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const {
    orders,
    totalOrders,
    isLoading,
    isFetching,
    error,
    searchOrders,
    refetch,
  } = useSearchOrders({
    initialPageSize: pageSize,
  });

  // Initial load and search when debounced term changes
  useEffect(() => {
    searchOrders({
      searchTerm: debouncedSearchTerm || '',
      page,
      pageSize,
    });
  }, [debouncedSearchTerm, page, pageSize, searchOrders]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleOrderClick = useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedOrderId(null);
  }, []);

  const headerActions: PageHeaderAction[] = [];

  if (error) {
    return (
      <div className="p-6">
        <PageHeader
          title="Orders"
          description="Manage and view all orders"
          actions={headerActions}
        />
        <OrderListErrorState error={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Orders"
        description="Manage and view all orders"
        actions={headerActions}
      />

      <div className="bg-content1 rounded-lg shadow-sm border border-default-200">
        <CustomTableHeader
          searchValue={searchTerm}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search orders by number, buyer, or distributor..."
          isLoading={isLoading || isFetching}
        />

        <OrderTable
          orders={orders}
          isLoading={isLoading || isFetching}
          onOrderClick={handleOrderClick}
        />

        {totalOrders > 0 && (
          <OrderPagination
            currentPage={page}
            totalPages={Math.ceil(totalOrders / pageSize)}
            totalItems={totalOrders}
            onPageChange={handlePageChange}
            isLoading={isFetching}
          />
        )}
      </div>

      {/* Order Details Side Panel */}
      <OrderDetailsPanel
        orderId={selectedOrderId || ''}
        isOpen={!!selectedOrderId}
        onClose={handleClosePanel}
      />
    </div>
  );
};
